import React, { useContext } from "react";
import { froalaKey } from "src/froala_license";
import { VariantContextData } from "../variants/VariantBuilderContext";
import useFields from "../fields/useFields";
import useActionGroups from "../action_groups/useActionGroups";
import useActions from "../actions/useActions";
import useContentLists from "../content_lists/useContentLists";
import useContentToggle from "../content_toggles/useContentToggle";
import useProducts from "../products/useProducts";
import useProductRecommendations from "../product_recommendations/useProductRecommendations";
import useReviewRecommendations from "../review_recommendations/useReviewRecommendations";
import useSchedulerRecommendations from "../scheduler_recommendations/useSchedulerRecommendations";
import useEmbedRecommendations from "../embed_recommendations/useEmbedRecommendations";
import useReviews from "../reviews/useReviews";

const useElements = (Sections) => {
  const [contextData, builder] = useContext(VariantContextData);
  const { ActionGroups } = useActionGroups();
  const { Fields } = useFields();
  const { ContentLists } = useContentLists();
  const { ContentToggle } = useContentToggle(Sections);
  const { Actions } = useActions();
  const { Products } = useProducts();
  const { ProductRecommendations } = useProductRecommendations();
  const { ReviewRecommendations } = useReviewRecommendations();
  const { SchedulerRecommendations } = useSchedulerRecommendations();
  const { EmbedRecommendations } = useEmbedRecommendations();
  const { Reviews } = useReviews();

  let variant = contextData.objects.variant;

  // Two elements share a column slot by section_column_id in flex sections and
  // by the legacy `column` index otherwise. Reordering flex columns changes
  // positions without rewriting elements' `column`, so column-only matching
  // mis-groups siblings — always prefer section_column_id when present.
  const sameColumnGroup = (element, reference) =>
    reference.section_column_id
      ? element.section_column_id == reference.section_column_id
      : element.column == reference.column;

  const Elements = {
    create: async function (
      parent_section,
      element_type,
      column,
      options,
      template_id = null,
      draggablePosition = null,
      updates = null,
      focusOnCreate = false,
      forceRender = false
    ) {
      const section_column = Object.values(variant.section_columns || {}).find(
        (sc) =>
          sc.section_id == parent_section.id &&
          sc.position == column &&
          sc.toBeDeleted !== true
      );
      let position =
        draggablePosition ||
        Object.values(variant.elements).filter(
          (element) =>
            element.section_id == parent_section.id &&
            element.toBeDeleted !== true &&
            (section_column
              ? element.section_column_id == section_column.id
              : element.column == column)
        ).length + 1;

      const afterCreate = (element) => {
        Elements.addChildrenToBuilder(element, true);

        if (element.element_type == "row") {
          let step = variant.steps[parent_section.step_id];

          Sections.create(step, element, null, true, undefined, undefined, undefined, (section) => {
            document.dispatchEvent(
              new CustomEvent("selectObject", {
                detail: {
                  object_type: 'sections',
                  object_id: section.id
                },
              })
            )
          });
        }

        if (focusOnCreate) {
          const elementOffset = $("#element" + element.id).offset();

          if (elementOffset) {
            $([document.documentElement, document.body]).animate(
              {
                scrollTop: elementOffset.top - 150,
              },
              2000
            );
          }
        }
      };

      if (template_id && template_id > 0) {
        dispatchCustomEvent("createObjectFromTemplate", {
          object_type: "elements",
          template_id: template_id,
          parent: {
            variant_id: variant.id,
            section_id: parent_section.id,
            step_id: parent_section.step_id,
            position: position,
            column: column,
          },
          element_type: element_type,
          onReady: afterCreate,
        });
        return;
      }

      builder.create(
        "elements",
        {
          variant_id: variant.id,
          section_id: parent_section.id,
          section_column_id: section_column ? section_column.id : null,
          position: position,
          element_type: element_type,
          column: column,
          options: options,
          template_id: template_id,
        },
        {
          updates: updates,
          forceRender: forceRender,
          callback: afterCreate,
          skip_select: element_type == "row",
        }
      );
    },

    duplicate: async function (og_element, payload, skip_history) {
      let updates = [];

      if (skip_history !== true) {
        let nextElements = Object.values(variant.elements).filter(
          (element) =>
            element.section_id == og_element.section_id &&
            sameColumnGroup(element, og_element) &&
            element.position > og_element.position &&
            element.toBeDeleted !== true
        );

        nextElements.forEach((nextElement) => {
          let update = {
            object_type: "elements",
            object_id: nextElement.id,
            setting_name: "[position]",
            value: nextElement.position + 1,
            old_value: nextElement.position,
          };
          updates.push(update);
          builder.update([{ ...update, skip_history: true }]);
        });
      }

      let attributes = {
        section_id: og_element.section_id,
        section_column_id: og_element.section_column_id,
        column: og_element.column,
        position:
          skip_history !== true ? og_element.position + 1 : og_element.position,
        element_type: og_element.element_type,
        html: og_element.html,
        options: og_element.options,
        duplicated: true,
      };

      if (payload) {
        attributes = { ...attributes, ...payload };
      }

      return new Promise((resolve, reject) => {
        builder.create("elements", attributes, {
          updates: updates,
          callback: async function (element) {
            try {
              Elements.addChildrenToBuilder(element, skip_history);

              // duplicate fields
              let element_parent_fields = Object.values(variant.fields).filter(
                (field) =>
                  field.element_id == og_element.id &&
                  field.toBeDeleted !== true &&
                  field.field_id == null
              );

              // duplicate content list items
              let content_list_items = Object.values(
                variant.content_list_items
              ).filter(
                (list_item) =>
                  list_item.element_id == og_element.id &&
                  list_item.toBeDeleted !== true
              );

              // duplicate content toggle items
              let content_toggle_items = Object.values(
                variant.content_toggle_items
              ).filter(
                (toggle_item) =>
                  toggle_item.element_id == og_element.id &&
                  toggle_item.toBeDeleted !== true
              );

              // duplicate actions
              let actions = Object.values(variant.actions).filter(
                (action) =>
                  action.contactable_id == og_element.id &&
                  action.contactable_type == "Element" &&
                  action.toBeDeleted !== true
              );

              // duplicate action groups
              let action_groups = Object.values(variant.action_groups).filter(
                (action_group) =>
                  action_group.element_id == og_element.id &&
                  action_group.toBeDeleted !== true
              );

              // duplicate row section
              let sections = Object.values(variant.sections).filter(
                (section) =>
                  section.element_id == og_element.id &&
                  section.toBeDeleted !== true &&
                  section.content_toggle_item_id == null
              );

              // The re-parented section may not be in this closure snapshot yet;
              // fall back to the source element's own section for the step_id.
              let parent_step_id =
                variant.sections[element.section_id]?.step_id ??
                variant.sections[og_element.section_id]?.step_id;

              // duplicate products
              let products = Object.values(variant.products).filter(
                (product) =>
                  product.element_id == og_element.id &&
                  product.toBeDeleted !== true
              );

              // duplicate product recommendations
              let product_recommendations = Object.values(
                variant.product_recommendations
              ).filter(
                (product_recommendation) =>
                  product_recommendation.element_id == og_element.id &&
                  product_recommendation.toBeDeleted !== true
              );

              // duplicate review recommendations
              let review_recommendations = Object.values(
                variant.review_recommendations || {}
              ).filter(
                (review_recommendation) =>
                  review_recommendation.element_id == og_element.id &&
                  review_recommendation.toBeDeleted !== true
              );

              let scheduler_recommendations = Object.values(
                variant.scheduler_recommendations || {}
              ).filter(
                (scheduler_recommendation) =>
                  scheduler_recommendation.element_id == og_element.id &&
                  scheduler_recommendation.toBeDeleted !== true
              );

              let embed_recommendations = Object.values(
                variant.embed_recommendations || {}
              ).filter(
                (embed_recommendation) =>
                  embed_recommendation.element_id == og_element.id &&
                  embed_recommendation.toBeDeleted !== true
              );

              // Duplicate fields in parallel and build ID mapping for quiz jump_field_id references
              const duplicatedFields = await Promise.all(
                element_parent_fields.map((field) =>
                  Fields.duplicate(
                    field,
                    { element_id: element.id },
                    true
                  ).then((newField) => ({
                    originalId: field.id,
                    newId: newField.id,
                  }))
                )
              );

              const duplicateFieldIdMapping = {};
              duplicatedFields.forEach(({ originalId, newId }) => {
                duplicateFieldIdMapping[originalId] = newId;
              });

              await Promise.all([
                ...content_list_items.map((list_item) => {
                  return ContentLists.duplicate(
                    list_item,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...content_toggle_items.map((toggle_item) => {
                  return ContentToggle.duplicate(
                    toggle_item,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...actions.map((action) => {
                  return Actions.duplicate(
                    action,
                    {
                      element_id: element.id,
                      contactable_id: element.id,
                      contactable_type: "Element",
                    },
                    true
                  );
                }),
                ...action_groups.map((action_group) => {
                  return ActionGroups.duplicate(
                    action_group,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...sections.map((section) => {
                  return Sections.duplicate(
                    section,
                    {
                      element_id: element.id,
                      step_id: parent_step_id,
                    },
                    true
                  );
                }),
                ...products.map((product) => {
                  return Products.duplicate(
                    product,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...product_recommendations.map((product_recommendation) => {
                  return ProductRecommendations.duplicate(
                    product_recommendation,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...review_recommendations.map((review_recommendation) => {
                  return ReviewRecommendations.duplicate(
                    review_recommendation,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...scheduler_recommendations.map((scheduler_recommendation) => {
                  return SchedulerRecommendations.duplicate(
                    scheduler_recommendation,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
                ...embed_recommendations.map((embed_recommendation) => {
                  return EmbedRecommendations.duplicate(
                    embed_recommendation,
                    {
                      element_id: element.id,
                    },
                    true
                  );
                }),
              ]);

              // Update jump_field_id references for quiz elements
              if (
                og_element.element_type === "quiz" &&
                Object.keys(duplicateFieldIdMapping).length > 0
              ) {
                let updates = [];

                // Get all original field_options for this element's fields
                element_parent_fields.forEach((originalField) => {
                  const originalFieldOptions = Object.values(
                    variant.field_options
                  ).filter(
                    (fo) =>
                      fo.field_id == originalField.id && fo.toBeDeleted !== true
                  );

                  originalFieldOptions.forEach((originalFieldOption) => {
                    const originalJumpFieldId =
                      originalFieldOption.jump_field_id;
                    if (
                      !originalJumpFieldId ||
                      originalJumpFieldId === "next_sub_question"
                    )
                      return;

                    const newJumpFieldId =
                      duplicateFieldIdMapping[parseInt(originalJumpFieldId)];
                    const newFieldId =
                      duplicateFieldIdMapping[originalField.id];

                    if (!newFieldId || !newJumpFieldId) return;

                    // Find the duplicated field_option by matching label and value
                    const duplicatedFieldOption = Object.values(
                      variant.field_options
                    ).find(
                      (fo) =>
                        fo.field_id == newFieldId &&
                        fo.label == originalFieldOption.label &&
                        fo.value == originalFieldOption.value &&
                        fo.toBeDeleted !== true
                    );

                    if (duplicatedFieldOption) {
                      updates.push({
                        object_type: "field_options",
                        object_id: duplicatedFieldOption.id,
                        setting_name: "[jump_field_id]",
                        value: newJumpFieldId,
                        skip_history: true,
                      });
                    }
                  });
                });

                if (updates.length > 0) {
                  builder.update(updates);
                }
              }

              resolve(element);
            } catch (error) {
              reject(error);
            }
          },
          skip_history: skip_history,
          skip_select: true,
        });
      });
    },

    remove: (removed_element) => {
      let message =
        "Are you sure you want to remove this element? Once your changes are saved, it will be irreversibly deleted.";
      let updates = [];

      let nextElements = Object.values(variant.elements).filter(
        (element) =>
          element.toBeDeleted !== true &&
          element.section_id == removed_element.section_id &&
          sameColumnGroup(element, removed_element) &&
          element.position > removed_element.position
      );

      nextElements.forEach((element) => {
        let update = {
          object_type: "elements",
          object_id: element.id,
          setting_name: "[position]",
          value: element.position - 1,
          old_value: element.position,
        };

        updates.push(update);
        builder.update([{ ...update, skip_history: true }]);
      });

      builder.remove("elements", removed_element.id, message, updates);

      const elementContainer = $(
        `.cf-cta-item-container[data-section-id="${removed_element.section_id}"][data-section-column="${removed_element.column}"]`
      );
    },

    moveUp: (moved_element) => {
      let updates = [];
      let elements = Object.values(variant.elements)
        .filter(
          (e) =>
            e.toBeDeleted !== true &&
            e.section_id == moved_element.section_id &&
            sameColumnGroup(e, moved_element)
        )
        .sort((a, b) => a.position - b.position);
      let new_value = moved_element.position - 1;

      if (elements.filter((e) => e.position == new_value)[0]) {
        updates.push({
          object_type: "elements",
          object_id: moved_element.id,
          setting_name: "[position]",
          value: new_value,
          old_value: moved_element.position,
        });

        elements
          .filter((e) => e.position == moved_element.position - 1)
          .forEach((element) => {
            updates.push({
              object_type: "elements",
              object_id: element.id,
              setting_name: "[position]",
              value: element.position + 1,
              old_value: element.position,
            });
          });

        builder.update(updates);
      }
    },

    moveDown: (moved_element) => {
      let updates = [];
      let elements = Object.values(variant.elements)
        .filter(
          (e) =>
            e.toBeDeleted !== true &&
            e.section_id == moved_element.section_id &&
            sameColumnGroup(e, moved_element)
        )
        .sort((a, b) => a.position - b.position);
      let new_value = moved_element.position + 1;

      if (elements.filter((e) => e.position == new_value)[0]) {
        updates.push({
          object_type: "elements",
          object_id: moved_element.id,
          setting_name: "[position]",
          value: new_value,
          old_value: moved_element.position,
        });

        elements
          .filter((e) => e.position == moved_element.position + 1)
          .forEach((element) => {
            updates.push({
              object_type: "elements",
              object_id: element.id,
              setting_name: "[position]",
              value: element.position - 1,
              old_value: element.position,
            });
          });

        builder.update(updates);
      }
    },

    addChildrenToBuilder: (element, skip_history) => {
      builder.addObjectToBuilder("elements", element, skip_history);
      ctas.builder.fonts.load_used_fonts(`#element${element.id}`);

      if (Object.keys(element).includes("fields")) {
        Object.values(element.fields).forEach((field) => {
          Fields.addToBuilder(field, skip_history);
        });
      }

      if (Object.keys(element).includes("content_list_items")) {
        Object.values(element.content_list_items).forEach((list_item) => {
          builder.addObjectToBuilder(
            "content_list_items",
            list_item,
            skip_history
          );
        });
      }

      if (Object.keys(element).includes("products")) {
        Object.values(element.products).forEach((product) => {
          builder.addObjectToBuilder("products", product, skip_history);

          Object.values(product.product_variants).forEach((product_variant) => {
            builder.addObjectToBuilder(
              "product_variants",
              product_variant,
              skip_history
            );
          });
        });
      }

      if (Object.keys(element).includes("product_recommendations")) {
        Object.values(element.product_recommendations).forEach(
          (product_recommendation) => {
            builder.addObjectToBuilder(
              "product_recommendations",
              product_recommendation,
              skip_history
            );

            Object.values(product_recommendation.products || {}).forEach(
              (product) => {
                builder.addObjectToBuilder("products", product, skip_history);
              }
            );
          }
        );
      }

      if (Object.keys(element).includes("review_recommendations")) {
        Object.values(element.review_recommendations).forEach(
          (review_recommendation) => {
            builder.addObjectToBuilder(
              "review_recommendations",
              review_recommendation,
              skip_history
            );
          }
        );
      }

      if (Object.keys(element).includes("scheduler_recommendations")) {
        Object.values(element.scheduler_recommendations).forEach(
          (scheduler_recommendation) => {
            builder.addObjectToBuilder(
              "scheduler_recommendations",
              scheduler_recommendation,
              skip_history
            );
          }
        );
      }

      if (Object.keys(element).includes("embed_recommendations")) {
        Object.values(element.embed_recommendations).forEach(
          (embed_recommendation) => {
            builder.addObjectToBuilder(
              "embed_recommendations",
              embed_recommendation,
              skip_history
            );

            Object.values(embed_recommendation.conditions || {}).forEach(
              (condition) => {
                builder.addObjectToBuilder(
                  "conditions",
                  condition,
                  skip_history
                );
              }
            );
          }
        );
      }

      if (Object.keys(element).includes("content_toggle_items")) {
        Object.values(element.content_toggle_items).forEach((list_item) => {
          builder.addObjectToBuilder(
            "content_toggle_items",
            list_item,
            skip_history
          );

          Object.values(list_item.conditions || {}).forEach((condition) => {
            builder.addObjectToBuilder("conditions", condition, skip_history);
          });
        });
      }

      if (element.actions) {
        Object.values(element.actions).forEach((action) => {
          builder.addObjectToBuilder("actions", action, skip_history);
        });
      }

      if (element.action_groups) {
        Object.values(element.action_groups).forEach((action_group) => {
          ActionGroups.addToBuilder(action_group, skip_history);
        });
      }

      if (Object.keys(element).includes("sections")) {
        Object.values(element.sections).forEach((section) => {
          builder.addObjectToBuilder("sections", section, skip_history);

          (section.section_columns
            ? Object.values(section.section_columns)
            : []
          ).forEach((section_column) => {
            builder.addObjectToBuilder(
              "section_columns",
              section_column,
              skip_history
            );
          });

          Object.values(section.elements || {}).forEach((element) => {
            Elements.addChildrenToBuilder(element, true);
          });
        });
      }
    },

    froalaEditor: function (website, element, target) {
      const FroalaEditor = froalaExtend(
        contextData.objects.website,
        contextData.objects.variant.fields,
        contextData.objects.variant.actions
      );

      let element_tag = document.querySelector(
        '#builder .cf-element[data-element-id="' + element.id + '"]'
      );
      let container = document.querySelector(
        '#builder .cf-section[section-id="' +
          element.section_id +
          '"] .cf-cta-item-container[data-section-id="' +
          element.section_id +
          '"][data-section-column="' +
          element.column +
          '"]'
      );
      let selectLabelPopover = element_tag.querySelector(
        '.select-label-popover[data-object_type="elements"][data-object_id="' +
          element.id +
          '"]'
      );

      if (target && !target.id) {
        target.id = "selected";
      }

      let brand_color_vibrant = "#0072f7";
      let brand_color_dark = "#000";
      let brand_color_light = "#fff";

      if (website && website.data) {
        brand_color_vibrant =
          website.data["brand_color_vibrant"] || brand_color_vibrant;
        brand_color_dark = website.data["brand_color_dark"] || brand_color_dark;
        brand_color_light =
          website.data["brand_color_light"] || brand_color_light;
      }

      let toolbarButtons;

      toolbarButtons = [
        "paragraphFormat",
        "bold",
        "italic",
        "underline",
        "strikeThrough",
        "insertLink",
        "fontFamily",
        "fontSize",
        "align",
        "lineHeight",
        "textColor",
        "backgroundColor",
        "customHTML",
      ];

      if (["button", "form"].includes(element.element_type)) {
        toolbarButtons.filter(
          (button) =>
            [
              "paragraphFormat",
              "backgroundColor",
              "insertLink",
              "align",
            ].includes(button) == false
        );
      }

      let toolbar_buttons_img = [
        "filestackReplace",
        "imageAlign",
        "imageLink",
        "linkOpen",
        "linkEdit",
        "linkRemove",
        "imageStyle",
        "imageSize",
        "-",
        "imageRemove",
        "imageAlt",
        "imageDisplay",
      ];

      if (element.element_type == "image") {
        // prettier-ignore
        toolbarButtons = ['align', 'filestack', 'insertLink', 'clearFormatting', 'customHTML'];
        toolbar_buttons_img = toolbar_buttons_img.filter(
          (tool) => tool !== "imageRemove"
        );
      }

      // prettier-ignore
      let color_codes = [
        '#FFFFFF', '#F2F2F2', '#bdc3c7', '#7f8c8d', '#6C7A89',
        '#3498db', '#0075ff', '#2C82C9', '#003FFF', '#475577', 
        '#34495e', '#28324E', '#191A1B', '#000000', '#4ECDC4', 
        '#1abc9c', '#2ecc71', '#27ae60', '#16a085', 
        '#019875', '#1E824C', '#F5D76E', '#FAC51C', 
        '#FBA026', '#e67e22', '#E25041', '#B8312F', 
        '#96281B', '#f624a2', '#BF55EC', '#9A12B3', 
        '#663399', brand_color_light, brand_color_vibrant, 
        brand_color_dark, 'REMOVE'
      ];

      // prettier-ignore
      let font_sizes = ['None', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '60', '72', '96'];

      // prettier-ignore

      let editor = element_tag.querySelector(".froala-editor");

      const updateBuilder = (new_value, skip_history) => {
        builder.update([
          {
            object_type: "elements",
            object_id: element.id,
            setting_name: "[html]",
            value: new_value,
            skip_history: skip_history ? skip_history : false,
          },
        ]);
        dispatchCustomEvent("selectObject", {
          object_type: "elements",
          object_id: element.id,
          editing: false,
        });
      };

      let froalaOptions = {
        attribution: false,
        toolbarInline: true,
        initOnClick: false,
        toolbarSticky: false,
        toolbarVisibleWithoutSelection: true,
        toolbarButtons: {
          moreText: {
            buttons: toolbarButtons,
            buttonsVisible: 6,
          },
          misc: {
            buttons: ["filestack", "personalize", "clearFormatting"],
            buttonsVisible: 3,
          },
        },
        charCounterCount: false,
        keepFormatOnDelete: true,
        enter: FroalaEditor.ENTER_BR,
        fontSize: font_sizes,
        fontFamily: builder_fonts,
        fontFamilySelection: true,
        imageEditButtons: toolbar_buttons_img,
        imageResize: false,
        colorsBackground: color_codes,
        colorsStep: 8,
        colorsText: color_codes,
        pastePlain: true,
        key: froalaKey(),
        linkStyles: {
          "cf-link-no-underline": "No underline",
          "cf-link-blue": "Blue hyperlink",
        },
        linkList: [],
        events: {
          initialized: function () {
            let thisFroala = this;
            $('.fr-element[contenteditable="true"]').attr(
              "data-gramm",
              "false"
            );

            $('.fr-command[data-cmd="fontFamily"]').on("click", function () {
              ctas.builder.fonts.froala_dropdown();
            });
          },
          "commands.before": function (cmd, e) {
            if (!this.selection.inEditor()) {
              this.selection.setAtEnd(this.el);
              this.selection.restore();
            }

            if (
              cmd == "customHTML" &&
              builder.displayFeatureGate(e, "Custom HTML")
            ) {
              this.events.trigger("blur", [], true);

              let modal_trigger = document.querySelector(
                'button[object_type="elements"][object_id="' +
                  element.id +
                  '"][setting_name="[html]"]'
              );
              if (modal_trigger) {
                modal_trigger.click();
              }
            }

            if (cmd == "filestack" || cmd == "filestackReplace") {
              froala.filestackUploading = true;
            }
          },
          "commands.after": function (cmd, e) {
            // list of commands that change content and should enable builder save button
            const commandsList = [
              "linkRemove",
              "linkInsert",
              "imageAlign",
              "imageSetAlt",
              "imageStyle",
              "imageSetSize",
            ];

            if (commandsList.includes(cmd)) {
              froala.events.trigger("blur", [], true);
            }
          },
          blur: function () {
            if (froala.filestackUploading !== true) {
              froala.html.cleanEmptyTags();
              let html = froala.html.get(true);
              html = html.replace('id="selected"', "");

              if (html) {
                console.log("froala.undo_index", froala.undo_index);
                updateBuilder(html, froala.undo_index <= 1);
                froala.destroy();
              }
            }
          },
          focus: function () {
            if (selectLabelPopover) {
              selectLabelPopover.style.display = "none";
            }
          },
        },
      };

      let froala = new FroalaEditor(editor, froalaOptions);

      let selected;
      if (froala) {
        selected = $(froala.$el).find("#selected")[0];
      }

      if (selected) {
        froala.events.trigger("focus", [], true);
        froala.events.trigger("selection.setAtEnd", [], true);
        froala.events.trigger("selection.restore", [], true);
        selected.removeAttribute("id");
      }

      window.sortables.forEach((sortable, i) => {
        if (
          sortable.section_id == element.section_id &&
          sortable.column == element.column
        ) {
          sortable.destroy();
        }
      });
      window.sortables = [];
    },

    reset: (object) => {
      if (
        confirm(
          "Are you sure you want to reset this element to the theme styling?"
        )
      ) {
        const element = variant.elements[object.id];
        let resettableSettings = {
          "box-shadow": null,
        };
        let updates = [];

        if (["headline", "text"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "font-family": null,
            "font-weight": null,
            "font-size": null,
            "font-size-mobile": null,
            "font-line-height": null,
            "font-line-height-mobile": null,
            "font-color": null,
          };
        }

        if (
          ["button", "survey", "product_hero", "products"].includes(
            element.element_type
          )
        ) {
          resettableSettings = {
            ...resettableSettings,
            "button-font": null,
            "button-font-weight": null,
            "button-font-size": null,
            "button-font-size-mobile": null,
            "button-text-color": null,
            "button–hover-text-color": null,
            "button-color": null,
            "button-hover-color": null,
            "button–hover-color": null,
            "button-border-style": null,
            "button-border-color": null,
            "button-border-width-left": null,
            "button-border-width-right": null,
            "button-border-width-top": null,
            "button-border-width-bottom": null,
            "button-hover-border-color": null,
            "button-radius": null,
            "button-shadow": null,
            "button-shadow-horizontal-distance": null,
            "button-shadow-vertical-distance": null,
            "button-shadow-blur-radius": null,
            "button-shadow-spread-radius": null,
            "button-shadow-color": null,
            "button-shadow-hover-color": null,
            "button-shadow-inset": null,
          };
        }

        if (["quiz"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "quiz-question-font-family": null,
            "quiz-question-font-weight": null,
            "quiz-question-font-size": null,
            "quiz-question-font-line-height": null,
            "quiz-question-font-color": null,
            "quiz-question-spacing": null,
            "quiz-question-distance-from-progress": null,
            "quiz-question-distance-from-progress-mobile": null,
            "quiz-question-spacing": null,
            "quiz-question-spacing-mobile": null,
            "quiz-question-font-size-mobile": null,
            "quiz-question-font-line-height-mobile": null,
            "quiz-description-font-size-mobile": null,
            "quiz-description-font-line-height-mobile": null,
            "quiz-description-font-family": null,
            "quiz-description-font-size": null,
            "quiz-description-font-weight": null,
            "quiz-description-font-line-height": null,
            "quiz-description-font-color": null,
            "loading-border-color": null,
            "loading-background-color": null,
            "label-image-placement": null,
            "label-image-placement-mobile": null,
            "quiz-options-layout": null,
            "quiz-options-layout-mobile": null,
            "quiz-option-height": null,
            "quiz-option-width-style": null,
            "quiz-option-width-value": null,
            "quiz-option-width-style-mobile": null,
            "quiz-option-width-value-mobile": null,
            "quiz-options-vertical-spacing": null,
            "quiz-options-vertical-spacing-mobile": null,
            "quiz-options-horizontal-spacing": null,
            "quiz-options-horizontal-spacing-mobile": null,
            "quiz-image-placement": null,
            "quiz-image-placement-mobile": null,
            "quiz-option-text-alignment": null,
            "quiz-option-text-alignment-mobile": null,
            "quiz-option-font-family": null,
            "quiz-option-font-weight": null,
            "quiz-option-font-size": null,
            "quiz-option-font-size-mobile": null,
            "quiz-option-font-line-height": null,
            "quiz-option-font-line-height-mobile": null,
            "quiz-option-font-color": null,
            "quiz-hover-option-font-color": null,
            "quiz-visual-placement": null,
            "quiz-visual-placement-mobile": null,
            "quiz-option-color": null,
            "quiz-hover-option-color": null,
            "quiz-option-image-width": null,
            "quiz-option-image-height": null,
            "quiz-option-visual-font-size": null,
            "quiz-option-visual-font-size-mobile": null,
            "quiz-option-visual-spacing": null,
            "quiz-option-visual-spacing-mobile": null,
            "quiz-option-color-size": null,
            "quiz-option-color-size-mobile": null,
            "quiz-option-background-color": null,
            "quiz-hover-option-background-color": null,
            "quiz-option-border-placement": null,
            "quiz-option-border-style": null,
            "quiz-option-border-color": null,
            "quiz-hover-option-border-color": null,
            "quiz-option-border-width-left": null,
            "quiz-option-border-width-right": null,
            "quiz-option-border-width-top": null,
            "quiz-option-border-width-bottom": null,
            "quiz-option-border-radius": null,
            "quiz-option-border-radius-top-left": null,
            "quiz-option-border-radius-top-right": null,
            "quiz-option-border-radius-bottom-left": null,
            "quiz-option-border-radius-bottom-right": null,
            "quiz-selected-option-style": null,
            "quiz-selected-option-border-style": null,
            "quiz-selected-option-border-color": null,
            "quiz-selected-option-border-width-left": null,
            "quiz-selected-option-border-width-right": null,
            "quiz-selected-option-border-width-top": null,
            "quiz-selected-option-border-width-bottom": null,
            "quiz-selected-option-font-color": null,
            "quiz-selected-option-background-color": null,
            "quiz-option-box-shadow": null,
            "quiz-option-box-shadow-horizontal-distance": null,
            "quiz-option-box-shadow-vertical-distance": null,
            "quiz-option-box-shadow-blur-radius": null,
            "quiz-option-box-shadow-color": null,
            "quiz-conditional-content-placement": null,
            "quiz-conditional-content-background-color": null,
            "quiz-conditional-content-border-style": null,
            "quiz-conditional-content-border-color": null,
            "quiz-conditional-content-border-width-left": null,
            "quiz-conditional-content-border-width-right": null,
            "quiz-conditional-content-border-width-top": null,
            "quiz-conditional-content-border-width-bottom": null,
            "quiz-conditional-content-border-radius": null,
            "quiz-conditional-content-border-radius-top-left": null,
            "quiz-conditional-content-border-radius-top-right": null,
            "quiz-conditional-content-border-radius-bottom-left": null,
            "quiz-conditional-content-border-radius-bottom-right": null,
            "quiz-conditional-content-padding": null,
            "quiz-progress-text-font": null,
            "quiz-progress-text-font-weight": null,
            "quiz-progress-text-size": null,
            "quiz-progress-text-size-mobile": null,
            "quiz-progress-text-color": null,
            "quiz-progress-outer-background-color": null,
            "quiz-progress-inner-background-color": null,
            "quiz-progress-border-style": null,
            "quiz-progress-border-color": null,
            "quiz-progress-border-width-left": null,
            "quiz-progress-border-width-right": null,
            "quiz-progress-border-width-top": null,
            "quiz-progress-border-width-bottom": null,
            "quiz-progress-outer-radius": null,
            "quiz-progress-outer-radius-top-left": null,
            "quiz-progress-outer-radius-top-right": null,
            "quiz-progress-outer-radius-bottom-left": null,
            "quiz-progress-outer-radius-bottom-right": null,
            "quiz-button-size": null,
            "quiz-button-size-mobile": null,
            "quiz-button-width": null,
            "quiz-button-font": null,
            "quiz-button-font-weight": null,
            "quiz-button-font-size": null,
            "quiz-button-font-size-mobile": null,
            "quiz-button-text-color": null,
            "quiz-button–hover-text-color": null,
            "quiz-button-color": null,
            "quiz-button–hover-color": null,
            "quiz-button-border-style": null,
            "quiz-button-border-color": null,
            "quiz-button-hover-border-color": null,
            "quiz-button-border-width-left": null,
            "quiz-button-border-width-right": null,
            "quiz-button-border-width-top": null,
            "quiz-button-border-width-bottom": null,
            "quiz-button-radius": null,
            "quiz-button-radius-top-left": null,
            "quiz-button-radius-top-right": null,
            "quiz-button-radius-bottom-left": null,
            "quiz-button-radius-bottom-right": null,
            "quiz-button-shadow": null,
            "quiz-button-shadow-horizontal-distance": null,
            "quiz-button-shadow-vertical-distance": null,
            "quiz-button-shadow-blur-radius": null,
            "quiz-button-shadow-spread-radius": null,
            "quiz-button-shadow-color": null,
            "quiz-back-button-text-color": null,
            "quiz-back-button–hover-text-color": null,
            "quiz-back-button-color": null,
            "quiz-back-button–hover-color": null,
            "quiz-back-button-border-style": "none",
            "quiz-back-button-border-color": null,
            "quiz-back-button-hover-border-color": null,
            "quiz-back-button-border-width-left": null,
            "quiz-back-button-border-width-right": null,
            "quiz-back-button-border-width-top": null,
            "quiz-back-button-border-width-bottom": null,
            "quiz-back-button-radius": null,
            "quiz-back-button-radius-top-left": null,
            "quiz-back-button-radius-top-right": null,
            "quiz-back-button-radius-bottom-left": null,
            "quiz-back-button-radius-bottom-right": null,
            "quiz-answer-width": null,
            "quiz-answer-width-value": null,
            "quiz-answer-field-size": null,
            "quiz-answer-field-size-mobile": null,
            "quiz-answer-field-font": null,
            "quiz-answer-field-font-weight": null,
            "quiz-answer-field-font-size": null,
            "quiz-answer-field-text-color": null,
            "quiz-answer-field-background-color": null,
            "quiz-answer-field-border-style": null,
            "quiz-answer-field-border-color": null,
          };

          // Get all fields associated with this quiz element and reset them
          let quizFields = Object.values(variant.fields).filter(
            (field) =>
              field.element_id == element.id && field.toBeDeleted !== true
          );

          quizFields.forEach((field) => {
            updates = Fields.reset(field, updates);
          });
        }

        if (["carousel"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "carousel-controls-style": null,
            "carousel-background-color": null,
            "carousel-arrow-size": null,
            "carousel-arrow-size-mobile": null,
            "carousel-arrow-controls-color": null,
            "carousel-indicator-size": null,
            "carousel-indicator-size-mobile": null,
            "carousel-indicator-controls-color": null,
            "carousel-indicator-active-border-style": null,
            "carousel-indicator-active-border-color": null,
            "carousel-indicator-active-border-width-left": null,
            "carousel-indicator-active-border-width-right": null,
            "carousel-indicator-active-border-width-top": null,
            "carousel-indicator-active-border-width-bottom": null,
            "carousel-indicator-border-radius": null,
            "carousel-indicator-border-radius-top-left": null,
            "carousel-indicator-border-radius-top-right": null,
            "carousel-indicator-border-radius-bottom-left": null,
            "carousel-indicator-border-radius-bottom-right": null,
          };
        }

        if (["collapse"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "collapse-heading-font-family": null,
            "collapse-heading-font-weight": null,
            "collapse-heading-font-size": null,
            "collapse-heading-font-size-mobile": null,
            "collapse-heading-font-color": null,
            "collapse-heading-background-color": null,
            "collapse-heading-border-style": null,
            "collapse-heading-border-color": null,
            "collapse-heading-border-width-left": null,
            "collapse-heading-border-width-right": null,
            "collapse-heading-border-width-top": null,
            "collapse-heading-border-width-bottom": null,
            "collapse-heading-radius": null,
            "collapse-heading-radius-top-left": null,
            "collapse-heading-radius-top-right": null,
            "collapse-heading-radius-bottom-left": null,
            "collapse-heading-radius-bottom-right": null,
            "collapse-items-distance": null,
            "collapse-content-background-color": null,
            "collapse-shadow": null,
            "collapse-shadow-horizontal-distance": null,
            "collapse-shadow-vertical-distance": null,
            "collapse-shadow-blur-radius": null,
            "collapse-shadow-spread-radius": null,
            "collapse-shadow-color": null,
          };
        }

        if (["form"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "field-font": null,
            "field-font-weight": null,
            "field-font-size": null,
            "field-font-size-mobile": null,
            "field-text-color": null,
            "field-label-font-family": null,
            "field-label-font-weight": null,
            "field-label-font-size": null,
            "field-label-font-size-mobile": null,
            "label-color": null,
            "field-rounded": null,
            "field-radius-top-left": null,
            "field-radius-top-right": null,
            "field-radius-bottom-left": null,
            "field-radius-bottom-right": null,
            "field-background-color": null,
            "field-border-style": null,
            "field-border-color": null,
            "field-border-width-left": null,
            "field-border-width-right": null,
            "field-border-width-top": null,
            "field-border-width-bottom": null,
            "field-handle-shape": null,
            "field-handle-color": null,
            "field-handle-border-color": null,
          };
        }

        if (["icon"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "icon-font-size": null,
            "icon-font-size-mobile": null,
            "icon-font-line-height": null,
            "icon-font-line-height-mobile": null,
            "icon-font-color": null,
            "icon-font-color-mobile": null,
            "icon-max-width-style": null,
            "icon-max-width-value": null,
            "icon-max-width-style-mobile": null,
            "icon-max-width-value-mobile": null,
            "icon-border-style": null,
            "icon-border-color": null,
            "icon-hover-border-color": null,
            "icon-border-width-left": null,
            "icon-border-width-right": null,
            "icon-border-width-top": null,
            "icon-border-width-bottom": null,
            "icon-border-radius": null,
            "icon-border-radius-top-left": null,
            "icon-border-radius-top-right": null,
            "icon-border-radius-bottom-left": null,
            "icon-border-radius-bottom-right": null,
            "icon-border-shadow": null,
            "icon-border-shadow-horizontal-distance": null,
            "icon-border-shadow-vertical-distance": null,
            "icon-border-shadow-blur-radius": null,
            "icon-border-shadow-spread-radius": null,
            "icon-border-shadow-color": null,
            "icon-hover-shadow-color": null,
            "icon-border-shadow-inset": null,
          };
        }

        if (["list"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            list_visual_type: null,
            list_icon: null,
            list_emoji: null,
            list_image: null,
            "visual-type-font-size": null,
            "visual-type-font-size-mobile": null,
            "visual-type-font-color": null,
            "visual-type-image-width": null,
            "visual-type-image-width-mobile": null,
            "content-font-family": null,
            "content-font-weight": null,
            "content-font-size": null,
            "content-font-size-mobile": null,
            "content-font-line-height": null,
            "content-font-line-height-mobile": null,
            "content-font-color": null,
            "content-box-shadow": null,
            "content-box-shadow-horizontal-distance": null,
            "content-box-shadow-vertical-distance": null,
            "content-box-shadow-blur-radius": null,
            "list-content-box-shadow-color": null,
            "list-visual-type-spacing": null,
            "list-visual-type-spacing-mobile": null,
            "list-items-divider-style": null,
            "list-items-divider-border-width": null,
            "list-items-divider-color": null,
            "list-background": null,
            "list-border-style": null,
            "list-border-color": null,
            "list-border-width-left": null,
            "list-border-width-right": null,
            "list-border-width-top": null,
            "list-border-width-bottom": null,
            "list-border-radius": null,
            "list-border-radius-top-left": null,
            "list-border-radius-top-right": null,
            "list-border-radius-bottom-left": null,
            "list-border-radius-bottom-right": null,
            "list-container-shadow": null,
            "list-container-shadow-horizontal-distance": null,
            "list-container-shadow-vertical-distance": null,
            "list-container-shadow-blur-radius": null,
            "list-container-shadow-spread-radius": null,
            "list-container-shadow-color": null,
            "list-container-shadow-inset": null,
          };
        }

        if (["navigation"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "nav-background-color": null,
            "nav-layout": null,
            "nav-links-layout": null,
            "nav-width": null,
            "nav-width-mobile": null,
            "nav-spacing": null,
            "nav-spacing-mobile": null,
            "nav-links-spacing": null,
            "nav-links-spacing-mobile": null,
            "nav-links-font-family": null,
            "nav-links-font-weight": null,
            "nav-links-font-size": null,
            "nav-links-font-size-mobile": null,
            "nav-links-font-color": null,
            "nav-links-hover-color": null,
            "nav-hamburger": null,
            "nav-hamburger-color": null,
            "nav-border-style": null,
            "nav-border-color": null,
            "nav-border-width-left": null,
            "nav-border-width-right": null,
            "nav-border-width-top": null,
            "nav-border-width-bottom": null,
            "nav-border-radius": null,
            "nav-border-radius-top-left": null,
            "nav-border-radius-top-right": null,
            "nav-border-radius-bottom-left": null,
            "nav-border-radius-bottom-right": null,
            "nav-menu-background-color": null,
            "nav-menu-padding": null,
            "nav-menu-border-style": null,
            "nav-menu-border-color": null,
            "nav-menu-border-width-left": null,
            "nav-menu-border-width-right": null,
            "nav-menu-border-width-top": null,
            "nav-menu-border-width-bottom": null,
            "nav-menu-border-radius": null,
            "nav-menu-border-radius-top-left": null,
            "nav-menu-border-radius-top-right": null,
            "nav-menu-border-radius-bottom-left": null,
            "nav-menu-border-radius-bottom-right": null,
            "nav-menu-shadow": null,
            "nav-menu-shadow-horizontal-distance": null,
            "nav-menu-shadow-vertical-distance": null,
            "nav-menu-shadow-blur-radius": null,
            "nav-menu-shadow-spread-radius": null,
            "nav-menu-shadow-color": null,
            "nav-menu-shadow-inset": null,
            "control-style": null,
            "control-style-mobile": null,
            "image-width": null,
            "image-width-mobile": null,
            "image-height": null,
            "image-height-mobile": null,
          };
        }

        if (["product_hero"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "products-item-width": null,
            "products-item-width-mobile": null,
            "products-item-content-width": null,
            "products-item-content-width-mobile": null,
            "products-item-image-max-width": null,
            "products-item-image-max-width-mobile": null,
            "products-padding": null,
            "products-padding-mobile": null,
            "products-content-type-spacing": null,
            "products-content-type-spacing-mobile": null,
            "products-item-background-color": null,
            "products-item-border-style": null,
            "products-item-border-color": null,
            "products-item-border-width-left": null,
            "products-item-border-width-right": null,
            "products-item-border-width-top": null,
            "products-item-border-width-bottom": null,
            "products-item-radius": null,
            "products-item-radius-top-left": null,
            "products-item-radius-top-right": null,
            "products-item-radius-bottom-left": null,
            "products-item-radius-bottom-right": null,
            "products-item-shadow": null,
            "products-item-shadow-horizontal-distance": null,
            "products-item-shadow-vertical-distance": null,
            "products-item-shadow-blur-radius": null,
            "products-item-shadow-spread-radius": null,
            "products-item-shadow-color": null,
            "products-item-shadow-inset": null,
            "products-item-image-height": null,
            "products-carousel-image-height": null,
            "products-carousel-image-height-mobile": null,
            "products-item-image-height-mobile": null,
            "products-item-title-font": null,
            "products-item-title-font-weight": null,
            "products-item-title-color": null,
            "products-item-title-size": null,
            "products-item-title-size-mobile": null,
            "products-item-title-line-height": null,
            "products-item-title-line-height-mobile": null,
            "products-item-price-font": null,
            "products-item-price-font-weight": null,
            "products-item-price-color": null,
            "products-item-discount-color": null,
            "products-item-price-size": null,
            "products-item-price-size-mobile": null,
            "products-item-description-font": null,
            "products-item-description-font-weight": null,
            "products-item-description-color": null,
            "products-item-description-size": null,
            "products-item-description-size-mobile": null,
            "products-item-description-line-height": null,
            "products-item-description-line-height-mobile": null,
            "products-content-label-font-family": null,
            "products-content-label-font-weight": null,
            "products-content-label-font-size": null,
            "products-content-label-font-size-mobile": null,
            "products-content-label-line-height": null,
            "products-content-label-line-height-mobile": null,
            "products-content-label-font-color": null,
            "products-variant-picker-button-minimum-width": null,
            "products-variant-picker-button-minimum-width-mobile": null,
            "products-variant-picker-font-family": null,
            "products-variant-picker-font-weight": null,
            "products-variant-picker-font-size": null,
            "products-variant-picker-font-size-mobile": null,
            "products-variant-picker-text-color": null,
            "products-variant-picker-text-hover-color": null,
            "products-variant-picker-background-color": null,
            "products-variant-picker-background-hover-color": null,
            "products-variant-picker-border-style": null,
            "products-variant-picker-border-color": null,
            "products-variant-picker-border-hover-color": null,
            "products-variant-picker-corner-rounding": null,
            "products-quantity-picker-minimum-width": null,
            "products-quantity-picker-minimum-width-mobile": null,
            "products-quantity-picker-font-family": null,
            "products-quantity-picker-font-weight": null,
            "products-quantity-picker-font-size": null,
            "products-quantity-picker-font-size-mobile": null,
            "products-quantity-picker-text-color": null,
            "products-quantity-picker-background-color": null,
            "products-quantity-picker-border-style": null,
            "products-quantity-picker-border-color": null,
            "products-quantity-picker-border-hover-color": null,
            "products-quantity-picker-corner-rounding": null,
            "products-subscription-choices-font-family": null,
            "products-subscription-choices-font-weight": null,
            "products-subscription-choices-font-size": null,
            "products-subscription-choices-font-size-mobile": null,
            "products-subscription-choices-text-color": null,
            "products-subscription-choices-text-hover-color": null,
            "products-subscription-choices-background-color": null,
            "products-subscription-border-style": null,
            "products-subscription-border-color": null,
            "products-subscription-border-hover-color": null,
            "products-subscription-radio-color": null,
            "products-subscription-radio-selected-color": null,
            "products-subscription-corner-rounding": null,
            "products-reviews-star-color": null,
            "products-reviews-star-size": null,
            "products-reviews-star-size-mobile": null,
            "products-reviews-font-family": null,
            "products-reviews-font-weight": null,
            "products-reviews-font-size": null,
            "products-reviews-font-size-mobile": null,
            "products-reviews-text-color": null,
            "products-offer-badge-background": null,
            "products-offer-badge-font-family": null,
            "products-offer-badge-font-family-weight": null,
            "products-offer-badge-font-size": null,
            "products-offer-badge-font-size-mobile": null,
            "products-offer-badge-text-color": null,
            "products-offer-badge-corner-top-left": null,
            "products-offer-badge-corner-top-right": null,
            "products-offer-badge-corner-bottom-left": null,
            "products-offer-badge-corner-bottom-right": null,
            "products-subscription-border-left": null,
            "products-subscription-border-right": null,
            "products-subscription-border-top": null,
            "products-subscription-border-bottom": null,
            "products-subscription-corner-top-left": null,
            "products-subscription-corner-top-right": null,
            "products-subscription-corner-bottom-left": null,
            "products-subscription-corner-bottom-right": null,
            "products-variant-picker-border-left": null,
            "products-variant-picker-border-right": null,
            "products-variant-picker-border-top": null,
            "products-variant-picker-border-bottom": null,
            "products-variant-picker-corner-top-left": null,
            "products-variant-picker-corner-top-right": null,
            "products-variant-picker-corner-bottom-left": null,
            "products-variant-picker-corner-bottom-right": null,
            "products-image-badge-background": null,
            "products-image-badge-font-family": null,
            "products-image-badge-font-family-weight": null,
            "products-image-badge-font-size": null,
            "products-image-badge-font-size-mobile": null,
            "products-image-badge-text-color": null,
            "products-image-badge-corner-rounding": null,
          };
        }

        if (["products"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "products-item-width": null,
            "products-item-width-mobile": null,
            "products-padding": null,
            "products-padding-mobile": null,
            "products-item-padding": null,
            "products-item-padding-mobile": null,
            "products-margin-vertical": null,
            "products-margin-vertical-mobile": null,
            "products-content-type-spacing": null,
            "products-content-type-spacing-mobile": null,
            "products-item-background-color": null,
            "products-item-border-style": null,
            "products-item-border-color": null,
            "products-item-border-width-left": null,
            "products-item-border-width-right": null,
            "products-item-border-width-top": null,
            "products-item-border-width-bottom": null,
            "products-item-radius": null,
            "products-item-radius-top-left": null,
            "products-item-radius-top-right": null,
            "products-item-radius-bottom-left": null,
            "products-item-radius-bottom-right": null,
            "products-item-shadow": null,
            "products-item-shadow-horizontal-distance": null,
            "products-item-shadow-vertical-distance": null,
            "products-item-shadow-blur-radius": null,
            "products-item-shadow-spread-radius": null,
            "products-item-shadow-color": null,
            "products-item-shadow-inset": null,
            "products-item-image-height": null,
            "products-carousel-image-height": null,
            "products-carousel-image-height-mobile": null,
            "products-image-corner-rounding": null,
            "products-item-image-height-mobile": null,
            "products-item-image-width": null,
            "products-item-image-width-mobile": null,
            "products-item-title-font": null,
            "products-item-title-font-weight": null,
            "products-item-title-color": null,
            "products-item-title-size": null,
            "products-item-title-size-mobile": null,
            "products-item-title-line-height": null,
            "products-item-title-line-height-mobile": null,
            "products-item-price-font": null,
            "products-item-price-font-weight": null,
            "products-item-price-color": null,
            "products-item-discount-color": null,
            "products-item-price-size": null,
            "products-item-price-size-mobile": null,
            "products-item-description-font": null,
            "products-item-description-font-weight": null,
            "products-item-description-color": null,
            "products-item-description-size": null,
            "products-item-description-size-mobile": null,
            "products-item-description-line-height": null,
            "products-item-description-line-height-mobile": null,
            "products-content-label-font-family": null,
            "products-content-label-font-weight": null,
            "products-content-label-font-size": null,
            "products-content-label-font-size-mobile": null,
            "products-content-label-line-height": null,
            "products-content-label-line-height-mobile": null,
            "products-content-label-font-color": null,
            "products-variant-picker-button-minimum-width": null,
            "products-variant-picker-button-minimum-width-mobile": null,
            "products-variant-picker-font-family": null,
            "products-variant-picker-font-weight": null,
            "products-variant-picker-font-size": null,
            "products-variant-picker-font-size-mobile": null,
            "products-variant-picker-text-color": null,
            "products-variant-picker-text-hover-color": null,
            "products-variant-picker-background-color": null,
            "products-variant-picker-background-hover-color": null,
            "products-variant-picker-border-style": null,
            "products-variant-picker-border-color": null,
            "products-variant-picker-border-hover-color": null,
            "products-variant-picker-corner-rounding": null,
            "products-variant-picker-border-left": null,
            "products-variant-picker-border-right": null,
            "products-variant-picker-border-top": null,
            "products-variant-picker-border-bottom": null,
            "products-variant-picker-corner-top-left": null,
            "products-variant-picker-corner-top-right": null,
            "products-variant-picker-corner-bottom-left": null,
            "products-variant-picker-corner-bottom-right": null,
            "products-quantity-picker-minimum-width": null,
            "products-quantity-picker-minimum-width-mobile": null,
            "products-quantity-picker-font-family": null,
            "products-quantity-picker-font-weight": null,
            "products-quantity-picker-font-size": null,
            "products-quantity-picker-font-size-mobile": null,
            "products-quantity-picker-background-color": null,
            "products-quantity-picker-text-color": null,
            "products-quantity-picker-border-style": null,
            "products-quantity-picker-border-color": null,
            "products-quantity-picker-border-hover-color": null,
            "products-quantity-picker-corner-rounding": null,
            "products-quantity-picker-border-left": null,
            "products-quantity-picker-border-right": null,
            "products-quantity-picker-border-top": null,
            "products-quantity-picker-border-bottom": null,
            "products-quantity-picker-corner-top-left": null,
            "products-quantity-picker-corner-top-right": null,
            "products-quantity-picker-corner-bottom-left": null,
            "products-quantity-picker-corner-bottom-right": null,
            "products-subscription-choices-font-family": null,
            "products-subscription-choices-font-weight": null,
            "products-subscription-choices-font-size": null,
            "products-subscription-choices-font-size-mobile": null,
            "products-subscription-choices-text-color": null,
            "products-subscription-choices-text-hover-color": null,
            "products-subscription-choices-background-color": null,
            "products-subscription-border-style": null,
            "products-subscription-border-color": null,
            "products-subscription-border-hover-color": null,
            "products-subscription-radio-color": null,
            "products-subscription-radio-selected-color": null,
            "products-subscription-corner-rounding": null,
            "products-subscription-border-left": null,
            "products-subscription-border-right": null,
            "products-subscription-border-top": null,
            "products-subscription-border-bottom": null,
            "products-subscription-corner-top-left": null,
            "products-subscription-corner-top-right": null,
            "products-subscription-corner-bottom-left": null,
            "products-subscription-corner-bottom-right": null,
            "products-reviews-star-color": null,
            "products-reviews-star-size": null,
            "products-reviews-star-size-mobile": null,
            "products-reviews-font-family": null,
            "products-reviews-font-weight": null,
            "products-reviews-font-size": null,
            "products-reviews-font-size-mobile": null,
            "products-reviews-text-color": null,
          };
        }

        if (["progress"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "progress-text-font": null,
            "progress-text-font-weight": null,
            "progress-text-size": null,
            "progress-text-size-mobile": null,
            "progress-text-color": null,
            "progress-outer-background-color": null,
            "progress-inner-background-color": null,
            "progress-inner-padding-mobile": null,
            "progress-border-style": null,
            "progress-border-color": null,
            "progress-border-width-left": null,
            "progress-border-width-right": null,
            "progress-border-width-top": null,
            "progress-border-width-bottom": null,
            "progress-outer-radius": null,
            "progress-outer-radius-top-left": null,
            "progress-outer-radius-top-right": null,
            "progress-outer-radius-bottom-left": null,
            "progress-outer-radius-bottom-right": null,
          };
        }

        if (["survey"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "visual-type-placement": null,
            "visual-type-font-size": null,
            "visual-type-font-color": null,
            "field-font": null,
            "field-font-weight": null,
            "field-font-size": null,
            "options-image-width": null,
            "options-image-height": null,
            "options-image-width-mobile": null,
            "options-image-height-mobile": null,
            "next-button-width": null,
            "next-button-padding": null,
            "next-button-font-family": null,
            "next-button-font-weight": null,
            "next-button-font-size": null,
            "next-button-text-color": null,
            "next-button–hover-text-color": null,
            "next-button-color": null,
            "next-button–hover-color": null,
            "next-button-border-style": null,
            "next-button-border-color": null,
            "next-button-hover-border-color": null,
            "next-button-border-width-left": null,
            "next-button-border-width-right": null,
            "next-button-border-width-top": null,
            "next-button-border-width-bottom": null,
            "next-button-radius": null,
            "next-button-radius-top-left": null,
            "next-button-radius-top-right": null,
            "next-button-radius-bottom-left": null,
            "next-button-radius-bottom-right": null,
            "next-button-shadow": null,
            "next-button-shadow-horizontal-distance": null,
            "next-button-shadow-vertical-distance": null,
            "next-button-shadow-blur-radius": null,
            "next-button-shadow-spread-radius": null,
            "next-button-shadow-color": null,
          };
        }

        if (["tabs"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "tabs-layout": null,
            "tabs-alignment": null,
            "tabs-width": null,
            "tabs-width-mobile": null,
            "tabs-border-style": null,
            "tabs-border-color": null,
            "tabs-border-color-hover": null,
            "tabs-border-color-active": null,
            "tabs-border-width": null,
            "tabs-border-width-left": null,
            "tabs-border-width-top": null,
            "tabs-border-width-bottom": null,
            "tabs-border-width-right": null,
            "tabs-radius": null,
            "tabs-radius-top-left": null,
            "tabs-radius-top-right": null,
            "tabs-radius-bottom-right": null,
            "tabs-radius-bottom-left": null,
            "tabs-padding-style": null,
            "tabs-padding-value": null,
            "tabs-padding-style-mobile": null,
            "tabs-padding-value-mobile": null,
            "tabs-background-color": null,
            "tabs-background-color-hover": null,
            "tabs-background-color-active": null,
            "tabs-font-family": null,
            "tabs-font-weight": null,
            "tabs-font-size": null,
            "tabs-font-size-mobile": null,
            "tabs-font-color": null,
            "tabs-font-color-hover": null,
            "tabs-font-color-active": null,
            "tabs-content-spacing-horizontal": null,
            "tabs-content-spacing-vertical": null,
            "tabs-content-spacing-horizontal-mobile": null,
            "tabs-content-spacing-vertical-mobile": null,
            "tabs-content-background-color": null,
            "tabs-content-border-style": null,
            "tabs-content-border-color": null,
            "tabs-content-border-width-left": null,
            "tabs-content-border-width-top": null,
            "tabs-content-border-width-bottom": null,
            "tabs-content-border-width-right": null,
            "tabs-content-radius": null,
            "tabs-content-radius-top-left": null,
            "tabs-content-radius-top-right": null,
            "tabs-content-radius-bottom-right": null,
            "tabs-content-radius-bottom-left": null,
          };
        }

        if (["timer"].includes(element.element_type)) {
          resettableSettings = {
            ...resettableSettings,
            "timer-number-font": null,
            "timer-number-font-weight": null,
            "timer-number-size": null,
            "timer-number-size-mobile": null,
            "timer-number-color": null,
            "timer-padding": null,
            "timer-padding-mobile": null,
            "timer-background-color": null,
            "timer-border-style": null,
            "timer-border-color": null,
            "timer-border-width-left": null,
            "timer-border-width-right": null,
            "timer-border-width-top": null,
            "timer-border-width-bottom": null,
            "timer-radius": null,
            "timer-radius-top-left": null,
            "timer-radius-top-right": null,
            "timer-radius-bottom-left": null,
            "timer-radius-bottom-right": null,
            "timer-shadow": null,
            "timer-shadow-horizontal-distance": null,
            "timer-shadow-vertical-distance": null,
            "timer-shadow-blur-radius": null,
            "timer-shadow-spread-radius": null,
            "timer-shadow-color": null,
            "timer-shadow-inset": null,
            "timer-unit-font": null,
            "timer-unit-font-weight": null,
            "timer-unit-color": null,
            "timer-unit-size": null,
            "timer-unit-size-mobile": null,
            "timer-message-color": null,
            "timer-message-font": null,
            "timer-message-font-weight": null,
            "timer-message-size": null,
            "timer-message-size-mobile": null,
          };
        }

        Object.keys(resettableSettings).forEach((setting_name) => {
          let setting_value = resettableSettings[setting_name];

          if (object.options[setting_name]) {
            updates.push({
              object_type: "elements",
              object_id: element.id,
              setting_name: `[options][${setting_name}]`,
              value: setting_value,
            });
          }
        });

        builder.update(updates);
      }
    },
  };

  let currentHook = Elements;
  return { Elements, currentHook };
};

export default useElements;
